/**
 * client/src/lib/add-key-links.ts
 *
 * ONE source of truth for the "go register an authenticator" deep link.
 *
 * The Security Keys page owns the whole enrolment flow — its
 * `useAddKeyQuery` hook (client/src/pages/security-keys/use-add-key-query.ts)
 * parses `?addKey=<mode>` and opens the unified `AddDeviceDialog` (#2210
 * module 03) pre-selected on that mode's mapped choice (`choiceForWireMode`,
 * #2210 module 04, AC-8) — the ceremony now runs in place, with no further
 * hand-off to `/auth/add-key`. Four surfaces link INTO it (dashboard policy
 * banner, the AAL gate panel, the AAL3 elevation notice, the recovery-setup
 * backup-passkey step), so the URL shape and the button copy live here rather
 * than being re-typed at each call site (DEVELOPMENT.md §Centralized
 * Modules). Points at `/security-keys` (not `/security-centre`) since
 * Security Keys split into its own route, sibling to Security Overview in the
 * section nav (security-section-tabs.tsx).
 *
 * Honesty (Friction Rule clause 2): `external_certified` is the ONLY mode that
 * can reach AAL3 — it is the one whose policy sets `requiresMds: true`
 * (shared/webauthn-registration-mode.ts), and AAL3 step-up admits only
 * credentials whose FIDO MDS attestation chain verified at registration
 * (`server/logic/webauthn/step-up.ts`, L0-4). So any link offered as an AAL3
 * REMEDY must suggest that mode; suggesting `external_standard` would send a
 * user through a full ceremony that cannot lift the gate they are stuck behind.
 * `choiceForWireMode` (module 04) maps BOTH `external_standard` and
 * `external_certified` onto the dialog's single "A security key" branch,
 * which always issues the tolerant `external_attempt` ceremony and checks
 * certification automatically (#2210 module 04 fix round — the
 * certified-vs-standard vocabulary this comment used to describe as a picker
 * choice no longer exists anywhere user-facing). The dialog's own honest,
 * outcome-conditional result copy — verified vs. not-yet-Maximum — is what
 * tells the user which outcome they actually got; this link only picks which
 * branch to pre-select, never an enforcement point (the server decides
 * assurance, as always).
 */

import type { RegistrationMode } from "@shared/webauthn-registration-mode";

/** Query key `useAddKeyQuery` parses. Shared so a rename can't desync writers. */
export const ADD_KEY_QUERY_PARAM = "addKey";

/** Deep link that opens the Security Keys picker on `mode`. */
export function addKeyHref(mode: RegistrationMode): string {
  return `/security-keys?${ADD_KEY_QUERY_PARAM}=${mode}`;
}

/**
 * The AAL3 remedy link — an attested external hardware key (the hardware basis
 * for Level 3, fresh). See the module header for why the mode is fixed.
 */
export const ADD_HARDWARE_KEY_HREF = addKeyHref("external_certified");

/** Shared CTA copy for that link — plain language, no jargon, no AAL codes. */
export const ADD_HARDWARE_KEY_LABEL = "Set up a security key";

/**
 * The second reachable AAL3 remedy link — an ordinary passkey, the second
 * (or third, …) one on the account. This is the dual-passkey basis's own
 * enrolment deep link (server/logic/webauthn/step-up-dual.ts): two DISTINCT,
 * each ≥24h-mature passkeys reach Level 3, fresh, without any attested
 * hardware key. Same "device" mode `backup-passkey-nudge.tsx` already links
 * to — pulled out here so the AAL gate rows can share it rather than
 * re-typing the mode string.
 */
export const ADD_SECOND_PASSKEY_HREF = addKeyHref("device");

/** Shared CTA copy for that link. */
export const ADD_SECOND_PASSKEY_LABEL = "Add a passkey";
